/**
 * "Update Metadata" — the rail's fourth button (client, 2026-09-18).
 *
 * The Edit dialog's form, without the dialog: pick a dataset and the eleven catalogue fields
 * open inline, with room to read the abstract rather than a modal's scroll. The dialog stays
 * where it was — the Datasets tab's Edit is a row action and belongs over its row — and both
 * render the same `DatasetMetadataForm`.
 *
 * **The row paints, the record fills in.** Exactly the dialog's rule, for the same reason:
 * the catalogue list carries title, abstract, domain, source, classification and visibility,
 * while licence, cadence, lineage and contact live only on `GET /datasets/{id}` — so the
 * form opens on what is already known and the rest lands a moment later, but never over a
 * field the reader has touched.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Btn } from '../../components/Btn';
import {
  ApiError,
  fetchConfig,
  fetchDataset,
  patchDataset,
  queryKeys,
  type DatasetPatch,
  type ManagedDatasetRow,
} from '../../lib/api';
import { foldRecord, patchBody } from './manager';
import { toast } from '../../lib/toast';
import { DatasetMetadataForm } from './DatasetMetadataForm';
import { DatasetPicker } from './DatasetPicker';

const fromRow = (row: ManagedDatasetRow): DatasetPatch => ({
  title: row.title,
  abstract: row.abstract,
  domain: row.domain,
  source_org: row.source_org,
  licence: null,
  cadence: null,
  lineage: null,
  contact: null,
  classification: row.classification,
  website_visible: row.website_visible,
});

export function MetadataPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<ManagedDatasetRow | null>(null);
  const [form, setForm] = useState<DatasetPatch | null>(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  const config = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const domains = config.data?.domains ?? [];

  const record = useQuery({
    queryKey: queryKeys.dataset(picked?.id ?? ''),
    queryFn: fetchDataset,
    enabled: picked !== null,
  });

  // The record's fields land once per pick; over an edit in progress only the still-empty
  // record-only fields are filled (`foldRecord`).
  useEffect(() => {
    const data = record.data;
    if (!data || !picked || data.id !== picked.id) return;
    setForm((f) => (f ? foldRecord(f, data, touched) : f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, record.data]);

  const pick = (row: ManagedDatasetRow) => {
    setPicked(row);
    setForm(fromRow(row));
    setTouched(false);
  };

  const save = async () => {
    if (!picked || !form) return;
    setBusy(true);
    try {
      await patchDataset(picked.id, patchBody(form, record.data?.id === picked.id));
      await qc.invalidateQueries({ queryKey: ['datasetsAll'] });
      void qc.invalidateQueries({ queryKey: ['datasets'] });
      void qc.invalidateQueries({ queryKey: ['dataset'] });
      toast(t('manager.ds.saved', { title: form.title }));
      setTouched(false);
    } catch (err) {
      const message = err instanceof ApiError || err instanceof Error ? err.message : '';
      toast(t('manager.ds.saveFailed', { message }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card mgr-panelcard" data-testid="mgr-metadata">
      <div className="card-head">
        <div className="card-head-text">
          <h3>{t('manager.meta.title')}</h3>
          <p className="sub">{t('manager.meta.sub')}</p>
        </div>
        {picked ? (
          <Btn size="sm" icon="chev" data-testid="mgr-metadata-change" onClick={() => setPicked(null)}>
            {t('manager.editData.change')}
          </Btn>
        ) : null}
      </div>

      {picked === null || form === null ? (
        <DatasetPicker
          value={null}
          onPick={pick}
          label={t('manager.meta.which')}
          hint={t('manager.meta.whichHint')}
          testId="mgr-meta-ds"
        />
      ) : (
        <>
          <p className="mgr-panel-target" data-testid="mgr-metadata-target">
            <strong>{picked.title}</strong> <code>{picked.id}</code>
          </p>
          <DatasetMetadataForm
            form={form}
            domains={domains}
            onChange={(patch) => {
              setTouched(true);
              setForm((f) => (f ? { ...f, ...patch } : f));
            }}
            idPrefix="mgr-meta"
          />
          <footer className="mgr-panel-foot">
            <Btn
              variant="primary"
              data-testid="mgr-metadata-save"
              disabled={busy || form.title.trim() === ''}
              onClick={() => void save()}
            >
              {busy ? t('manager.ds.saving') : t('manager.ds.save')}
            </Btn>
          </footer>
        </>
      )}
    </section>
  );
}

export default MetadataPanel;
